/**
 * IntegrationInterfaces Registry
 *
 * This file serves as the central registry for all integration interfaces.
 * Each integration exports:
 *   - IntegrationInterface: The main component for configuring the integration
 *   - IntegrationIcon: The icon component for the tile
 *   - integrationMeta: Metadata (id, name, description)
 *
 * To add a new integration:
 * 1. Create a new file: [Name]Interface.js (e.g., SlackInterface.js)
 * 2. Export the three required items following the contract in BaseIntegrationInterface.js
 * 3. Import and register it in this file
 */

import {
  BaseIntegrationInterface,
  BaseIntegrationIcon,
  DefaultIconFallback,
} from "./BaseIntegrationInterface";

import {
  FathomInterface,
  FathomIcon,
  fathomIntegrationMeta,
} from "./FathomInterface";

import {
  SlackInterface,
  SlackIcon,
  slackIntegrationMeta,
} from "./SlackInterface";

import {
  SarvamInterface,
  SarvamIcon,
  sarvamIntegrationMeta,
} from "./SarvamInterface";

import {
  IntercomInterface,
  IntercomIcon,
  intercomIntegrationMeta,
} from "./IntercomInterface";

import {
  TypeformInterface,
  TypeformIcon,
  typeformIntegrationMeta,
} from "./TypeformInterface";

import {
  AirtableInterface,
  AirtableIcon,
  airtableIntegrationMeta,
} from "./AirtableInterface";

import {
  DiscordInterface,
  DiscordIcon,
  discordIntegrationMeta,
} from "./DiscordInterface";

import {
  NotionInterface,
  NotionIcon,
  notionIntegrationMeta,
} from "./NotionInterface";

import {
  ZohoDeskInterface,
  ZohoDeskIcon,
  zohoDeskIntegrationMeta,
} from "./ZohoDeskInterface";

import {
  ZendeskInterface,
  ZendeskIcon,
  zendeskIntegrationMeta,
} from "./ZendeskInterface";

import {
  ZoomInterface,
  ZoomIcon,
  zoomIntegrationMeta,
} from "./ZoomInterface";

import {
  KoboInterface,
  KoboIcon,
  koboIntegrationMeta,
} from "./KoboInterface";

import {
  AppStoreReviewsInterface,
  AppStoreReviewsIcon,
  appStoreReviewsIntegrationMeta,
} from "./AppStoreReviewsInterface";
import {
  PlayStoreReviewsInterface,
  PlayStoreReviewsIcon,
  playStoreReviewsIntegrationMeta,
} from "./PlayStoreReviewsInterface";
import {
  SmartCompanyImportInterface,
  SmartCompanyImportIcon,
  smartCompanyImportIntegrationMeta,
} from "./SmartCompanyImportInterface";

// ============================================
// INTEGRATION REGISTRY
// ============================================
// Maps adapter IDs to their interface components and icons
// The ID must match the adapter id in EXTERNAL_IMPORT_ADAPTERS_CONFIG on backend

const integrationRegistry = {
  fathom: {
    Interface: FathomInterface,
    Icon: FathomIcon,
    meta: fathomIntegrationMeta,
  },
  slack: {
    Interface: SlackInterface,
    Icon: SlackIcon,
    meta: slackIntegrationMeta,
  },
  sarvam: {
    Interface: SarvamInterface,
    Icon: SarvamIcon,
    meta: sarvamIntegrationMeta,
  },
  intercom: {
    Interface: IntercomInterface,
    Icon: IntercomIcon,
    meta: intercomIntegrationMeta,
  },
  typeform: {
    Interface: TypeformInterface,
    Icon: TypeformIcon,
    meta: typeformIntegrationMeta,
  },
  airtable: {
    Interface: AirtableInterface,
    Icon: AirtableIcon,
    meta: airtableIntegrationMeta,
  },
  discord: {
    Interface: DiscordInterface,
    Icon: DiscordIcon,
    meta: discordIntegrationMeta,
  },
  notion: {
    Interface: NotionInterface,
    Icon: NotionIcon,
    meta: notionIntegrationMeta,
  },
  zohoDesk: {
    Interface: ZohoDeskInterface,
    Icon: ZohoDeskIcon,
    meta: zohoDeskIntegrationMeta,
  },
  zendesk: {
    Interface: ZendeskInterface,
    Icon: ZendeskIcon,
    meta: zendeskIntegrationMeta,
  },
  kobo: {
    Interface: KoboInterface,
    Icon: KoboIcon,
    meta: koboIntegrationMeta,
  },
  // Future integrations:
  // otter: {
  //   Interface: OtterInterface,
  //   Icon: OtterIcon,
  //   meta: otterIntegrationMeta,
  // },
  zoom: {
    Interface: ZoomInterface,
    Icon: ZoomIcon,
    meta: zoomIntegrationMeta,
  },
  appStoreReviews: {
    Interface: AppStoreReviewsInterface,
    Icon: AppStoreReviewsIcon,
    meta: appStoreReviewsIntegrationMeta,
  },
  playStoreReviews: {
    Interface: PlayStoreReviewsInterface,
    Icon: PlayStoreReviewsIcon,
    meta: playStoreReviewsIntegrationMeta,
  },
  smartCompanyImport: {
    Interface: SmartCompanyImportInterface,
    Icon: SmartCompanyImportIcon,
    meta: smartCompanyImportIntegrationMeta,
  },
};

// ============================================
// FACTORY FUNCTIONS
// ============================================

/**
 * Get the interface component for an integration
 * @param {string} integrationId - The integration ID (e.g., "fathom")
 * @returns {React.Component} The interface component, or BaseIntegrationInterface as fallback
 */
export const getIntegrationInterface = (integrationId) => {
  return integrationRegistry[integrationId]?.Interface || BaseIntegrationInterface;
};

/**
 * Get the icon component for an integration
 * @param {string} integrationId - The integration ID (e.g., "fathom")
 * @returns {React.Component} The icon component, or DefaultIconFallback as fallback
 */
export const getIntegrationIcon = (integrationId) => {
  return integrationRegistry[integrationId]?.Icon || null;
};

/**
 * Get the display name for an integration (e.g. "Play Store Reviews")
 * @param {string} integrationId - The integration ID (e.g., "playStoreReviews")
 * @returns {string} The display name, or the id itself when unregistered
 */
export const getIntegrationName = (integrationId) => {
  return integrationRegistry[integrationId]?.meta?.name || integrationId;
};

/**
 * Check if an integration has a custom interface registered
 * @param {string} integrationId - The integration ID
 * @returns {boolean}
 */
export const hasCustomInterface = (integrationId) => {
  return Boolean(integrationRegistry[integrationId]);
};

/**
 * Get all registered integration IDs
 * @returns {string[]}
 */
export const getRegisteredIntegrations = () => {
  return Object.keys(integrationRegistry);
};

// ============================================
// RE-EXPORTS
// ============================================
// Export base components for custom integrations to use

export {
  BaseIntegrationInterface,
  BaseIntegrationIcon,
  DefaultIconFallback,
} from "./BaseIntegrationInterface";

export {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  TileIconContainer,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";

// Export individual integrations for direct import if needed
export { FathomInterface, FathomIcon } from "./FathomInterface";
export { SlackInterface, SlackIcon } from "./SlackInterface";

export { SarvamInterface, SarvamIcon } from "./SarvamInterface";
export { IntercomInterface, IntercomIcon } from "./IntercomInterface";
export { TypeformInterface, TypeformIcon } from "./TypeformInterface";
export { AirtableInterface, AirtableIcon } from "./AirtableInterface";
export { DiscordInterface, DiscordIcon } from "./DiscordInterface";
export { NotionInterface, NotionIcon } from "./NotionInterface";
export { ZohoDeskInterface, ZohoDeskIcon } from "./ZohoDeskInterface";
export { ZendeskInterface, ZendeskIcon } from "./ZendeskInterface";
export { KoboInterface, KoboIcon } from "./KoboInterface";
export { ZoomInterface, ZoomIcon } from "./ZoomInterface";
export { AppStoreReviewsInterface, AppStoreReviewsIcon } from "./AppStoreReviewsInterface";
export { PlayStoreReviewsInterface, PlayStoreReviewsIcon } from "./PlayStoreReviewsInterface";
export { SmartCompanyImportInterface, SmartCompanyImportIcon } from "./SmartCompanyImportInterface";

// Export base preflight interface for custom integrations
export { BasePreflightInterface } from "./BasePreflightInterface";
